import {
  ProductHighlight,
  ProductHighlightDetail,
  ProductHighlightHead,
} from '@diamond/shared/types';
import { useQuery } from '@tanstack/react-query';
import shallow from 'zustand/shallow';

import { mapApiProductsToUI } from '../adapters/highlightAdapter';
import {
  getOneProductHighlight,
  getProductHighlightsHeads,
  getProductHighlightsLegacy,
  getProductsFromHighlight,
} from '../queries/products';
import { useAuthStore, useShipToStore } from '../stores';

/**
 * Hook: useGetHomepageHighlights
 *
 * Fetches homepage product highlights and returns them in the UI-ready shape
 * (`ProductHighlight[]`). The hook first fetches highlight heads and then
 * fetches items for each head, mapping API products to the UI product shape.
 *
 * @param options - Optional settings for the underlying query.
 *   - `enabled?: boolean` — when false, disables the query regardless of auth/warehouse state.
 *
 * @returns The React Query result for `ProductHighlight[]` which includes:
 *   - `data`: ProductHighlight[] (empty array placeholder until data is loaded)
 *   - `status`: request status ('idle' | 'loading' | 'error' | 'success')
 *   - `error`: Error | null
 *   - other React Query helpers (refetch, isFetching, isStale, etc.)
 *
 * Notes:
 * - Query is enabled only when Zustand has hydrated, `selectedShipToId` exists,
 *   and `session.access_token` is available (unless overridden by `options.enabled`).
 * - Stale time is 4 minutes and the query does not refetch on window focus.
 */
export const useGetHomepageHighlights = (options?: { enabled?: boolean }) => {
  const session = useAuthStore();
  const [selectedShipToId] = useShipToStore(
    (s) => [s.selectedShipToId],
    shallow
  );
  // Ensure we only run the query after Zustand has rehydrated from storage
  // For SSR we must NOT run client-only queries to avoid hydration mismatch.
  // On client, wait for hydration before enabling queries
  const hydrated =
    typeof window === 'undefined'
      ? false
      : useShipToStore?.persist?.hasHydrated?.() ?? false;

  const isNew = options?.enabled ?? true;

  const newQuery = useQuery<ProductHighlight>({
    queryKey: ['HomepageHighlights', selectedShipToId],
    enabled: hydrated && !!selectedShipToId && !!session.access_token && isNew,
    staleTime: 1000 * 60 * 4,
    refetchOnWindowFocus: false,
    notifyOnChangeProps: ['data', 'error', 'status'],
    placeholderData: [] as ProductHighlight,
    queryFn: async () => {
      const heads = await getProductHighlightsHeads(
        session.access_token,
        selectedShipToId
      );

      if (!heads || heads.length === 0) return [] as ProductHighlight;

      const size = 20;
      const page = 1;
      const itemsByHead = await Promise.all(
        heads.map((h: ProductHighlightHead) =>
          getProductsFromHighlight(session.access_token, h.id, {
            size,
            page,
            warehouse: selectedShipToId,
          })
        )
      );

      const result: ProductHighlight = heads.map(
        (h: ProductHighlightHead, idx) => ({
          id: h.id,
          title: h.title,
          index: h.index ?? idx,
          distribution_channel: h.distribution_channel ?? [],
          business_segmentation: h.business_segmentation ?? [],
          product_highlight_type: h.product_highlight_type,
          products: mapApiProductsToUI(itemsByHead[idx]?.data ?? []),
        })
      );

      return result;
    },
  });

  const legacyQuery = useQuery<ProductHighlight>({
    queryKey: ['HomepageHighlightsLegacy', selectedShipToId],
    enabled: hydrated && !!selectedShipToId && !!session.access_token && !isNew,
    staleTime: 1000 * 60 * 4,
    refetchOnWindowFocus: false,
    notifyOnChangeProps: ['data', 'error', 'status'],
    placeholderData: [] as ProductHighlight,
    queryFn: async () => {
      const response = await getProductHighlightsLegacy(
        session.access_token,
        selectedShipToId
      );
      return (response ?? []).map(
        (h: ProductHighlight[number], idx: number) => ({
          id: h.id,
          title: h.title,
          index: h.index ?? idx,
          distribution_channel: h.distribution_channel ?? [],
          business_segmentation: h.business_segmentation ?? [],
          product_highlight_type: h.product_highlight_type,
          products: mapApiProductsToUI(h.products ?? []),
        })
      ) as ProductHighlight;
    },
  });

  return isNew ? newQuery : legacyQuery;
};

/**
 * Hook: useGetOneProductHighlight
 *
 * Fetches a single product highlight detail scoped to a warehouse/ship-to id.
 *
 * @param id - The highlight id to fetch.
 * @param warehouse - The warehouse/ship-to id used to scope the request.
 *
 * @returns The React Query result for `ProductHighlightDetail`, including:
 *   - `data`: ProductHighlightDetail | undefined
 *   - `status`: request status ('idle' | 'loading' | 'error' | 'success')
 *   - `error`: Error | null
 *   - other React Query helpers (refetch, isFetching, etc.)
 */
export const useGetOneProductHighlight = (id: string, warehouse: string) => {
  const session = useAuthStore();

  return useQuery<ProductHighlightDetail>({
    queryKey: ['OneProductHighlight', id, warehouse],
    queryFn: () => getOneProductHighlight(session.access_token, id, warehouse),
    enabled: !!id && !!warehouse && !!session.access_token,
    staleTime: 1000 * 60 * 4,
    refetchOnWindowFocus: false,
  });
};
